import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { useTheme } from "next-themes";
import { chartColors } from "@/lib/chartTheme";
import { WhoopConnect } from "@/components/WhoopConnect";
import { useAuth } from "@/context/AuthContext";
import { fmtDistance, fmtStoredPace, distUnit, miToDisplay } from "@/lib/units";
import { Plus, Trophy, Dumbbell, MapPin, Camera, Trash2 } from "lucide-react";

export default function Progress() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { resolvedTheme } = useTheme();
  const c = chartColors(resolvedTheme !== "light");
  const [summary, setSummary] = useState(null);
  const [weights, setWeights] = useState([]);
  const [prs, setPrs] = useState([]);
  const [history, setHistory] = useState([]);
  const [runs, setRuns] = useState([]);
  const [wInput, setWInput] = useState("");
  const [prForm, setPrForm] = useState({ exercise: "", value: "" });
  const [photos, setPhotos] = useState([]);
  const [adherence, setAdherence] = useState([]);
  const [adForm, setAdForm] = useState({ adherence_pct: 80, note: "" });
  const [uploading, setUploading] = useState(false);

  const loadAll = () => {
    api.get("/progress/summary").then(({ data }) => setSummary(data)).catch(() => {});
    api.get("/progress/weight").then(({ data }) => setWeights(data)).catch(() => {});
    api.get("/progress/pr").then(({ data }) => setPrs(data)).catch(() => {});
    api.get("/progress/workout-history").then(({ data }) => setHistory(data)).catch(() => {});
    api.get("/runs").then(({ data }) => setRuns(data)).catch(() => {});
    api.get("/progress/photo").then(({ data }) => setPhotos(data)).catch(() => {});
    api.get("/progress/nutrition-adherence").then(({ data }) => setAdherence(data)).catch(() => {});
  };
  useEffect(loadAll, []);

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Image too large (max 5MB)"); return; }
    setUploading(true);
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await api.post("/progress/photo", { image_base64: reader.result });
        toast.success("Photo added"); loadAll();
      } catch (err) { toast.error("Upload failed"); }
      finally { setUploading(false); }
    };
    reader.onerror = () => { setUploading(false); toast.error("Could not read file"); };
    reader.readAsDataURL(file);
  };
  const delPhoto = async (id) => { try { await api.delete(`/progress/photo/${id}`); loadAll(); } catch (e) {} };
  const logAdherence = async () => {
    const today = new Date().toISOString().slice(0, 10);
    await api.post("/progress/nutrition-adherence", { date: today, adherence_pct: parseInt(adForm.adherence_pct), note: adForm.note });
    setAdForm({ adherence_pct: 80, note: "" }); toast.success("Logged"); loadAll();
  };

  const logWeight = async () => {
    if (!wInput) return;
    await api.post("/progress/weight", { weight_lb: parseFloat(wInput) });
    setWInput(""); toast.success("Weight logged"); loadAll();
  };
  const logPr = async () => {
    if (!prForm.exercise || !prForm.value) return;
    await api.post("/progress/pr", prForm);
    setPrForm({ exercise: "", value: "" }); toast.success("PR saved"); loadAll();
  };

  const chartData = weights.map((w) => ({ date: new Date(w.created_at).toLocaleDateString([], { month: "short", day: "numeric" }), lb: w.weight_lb }));

  return (
    <AppShell title="Progress">
      <WhoopConnect />
      {summary && (
        <div className="grid grid-cols-4 gap-2 mb-4">
          {[["Workouts", summary.workouts_completed],["Runs", summary.runs],[distUnit(user).toUpperCase(), Math.round(miToDisplay(summary.total_distance_mi, user))],["PRs", summary.prs]].map(([l, v]) => (
            <Card key={l} className="rounded-xl bg-card border-border p-2 text-center"><p className="font-mono text-xl font-bold">{v}</p><p className="text-[10px] text-muted-foreground">{l}</p></Card>
          ))}
        </div>
      )}

      <Tabs defaultValue="weight">
        <TabsList className="bg-card border border-border w-full overflow-x-auto rd-scrollbar">
          <TabsTrigger value="weight" data-testid="ptab-weight" className="flex-1 data-[state=active]:bg-[#ccff0c] data-[state=active]:text-black">Weight</TabsTrigger>
          <TabsTrigger value="prs" data-testid="ptab-prs" className="flex-1 data-[state=active]:bg-[#ccff0c] data-[state=active]:text-black">PRs</TabsTrigger>
          <TabsTrigger value="photos" data-testid="ptab-photos" className="flex-1 data-[state=active]:bg-[#ccff0c] data-[state=active]:text-black">Photos</TabsTrigger>
          <TabsTrigger value="nutrition" data-testid="ptab-nutrition" className="flex-1 data-[state=active]:bg-[#ccff0c] data-[state=active]:text-black">Diet</TabsTrigger>
          <TabsTrigger value="history" data-testid="ptab-history" className="flex-1 data-[state=active]:bg-[#ccff0c] data-[state=active]:text-black">Log</TabsTrigger>
        </TabsList>

        <TabsContent value="weight" className="mt-4 space-y-4">
          <div className="flex gap-2">
            <Input data-testid="weight-input" type="number" value={wInput} onChange={(e) => setWInput(e.target.value)} placeholder="Log weight (lb)" className="bg-card border-border h-11" />
            <Button data-testid="log-weight" onClick={logWeight} className="h-11 bg-[#ccff0c] text-black hover:bg-[#d7ff3a] font-semibold">Log</Button>
          </div>
          {chartData.length > 1 ? (
            <Card data-testid="progress-weight-chart" className="rounded-2xl bg-card border-border p-3">
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={chartData}>
                  <CartesianGrid stroke={c.grid} />
                  <XAxis dataKey="date" tick={{ fill: c.tick, fontSize: 11 }} />
                  <YAxis tick={{ fill: c.tick, fontSize: 11 }} domain={["dataMin - 2", "dataMax + 2"]} />
                  <Tooltip contentStyle={{ background: c.tooltipBg, border: `1px solid ${c.tooltipBorder}`, borderRadius: 8 }} labelStyle={{ color: c.tooltipLabel }} />
                  <Line type="monotone" dataKey="lb" stroke={c.lime} strokeWidth={2.5} dot={{ r: 3, fill: c.lime }} />
                </LineChart>
              </ResponsiveContainer>
            </Card>
          ) : (
            <p className="text-muted-foreground text-sm text-center py-6">Log your weight a few times to see your trend.</p>
          )}
        </TabsContent>

        <TabsContent value="prs" className="mt-4 space-y-3">
          <Dialog>
            <DialogTrigger asChild><Button data-testid="add-pr" className="w-full bg-secondary border border-border text-foreground hover:border-[#ccff0c]/40"><Plus className="h-4 w-4 mr-1" /> Add PR</Button></DialogTrigger>
            <DialogContent className="bg-popover border-border max-w-[400px]">
              <DialogHeader><DialogTitle className="font-display">New personal record</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label className="text-xs">Exercise</Label><Input data-testid="pr-exercise" value={prForm.exercise} onChange={(e) => setPrForm((f) => ({ ...f, exercise: e.target.value }))} className="bg-card border-border h-10" placeholder="Back Squat" /></div>
                <div><Label className="text-xs">Value</Label><Input data-testid="pr-value" value={prForm.value} onChange={(e) => setPrForm((f) => ({ ...f, value: e.target.value }))} className="bg-card border-border h-10" placeholder="140kg x 3" /></div>
                <Button data-testid="save-pr" onClick={logPr} className="w-full bg-[#ccff0c] text-black hover:bg-[#d7ff3a] font-semibold">Save PR</Button>
              </div>
            </DialogContent>
          </Dialog>
          {prs.length === 0 ? <p className="text-muted-foreground text-sm text-center py-6">No PRs yet. Go set one.</p> : prs.map((p) => (
            <Card key={p.id} className="rounded-xl bg-card border-border p-3 flex items-center gap-3"><Trophy className="h-5 w-5 text-[#ccff0c]" /><div><p className="font-semibold">{p.exercise}</p><p className="text-sm text-muted-foreground font-mono">{p.value}</p></div></Card>
          ))}
        </TabsContent>

        <TabsContent value="photos" className="mt-4 space-y-3">
          <label data-testid="photo-upload-label" className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-border bg-card py-8 cursor-pointer hover:border-[#ccff0c]/40 transition-colors">
            {uploading ? (
              <span className="text-sm text-muted-foreground">Uploading…</span>
            ) : (
              <>
                <Camera className="h-7 w-7 text-[#ccff0c] mb-2" />
                <span className="text-sm text-muted-foreground">Add a progress photo</span>
              </>
            )}
            <input data-testid="photo-upload-input" type="file" accept="image/*" className="hidden" onChange={uploadPhoto} />
          </label>
          {photos.length === 0 ? (
            <p className="text-muted-foreground text-sm text-center py-4">No photos yet. Track your transformation.</p>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              {photos.map((ph) => (
                <div key={ph.id} data-testid={`photo-${ph.id}`} className="relative rounded-xl overflow-hidden border border-border">
                  <img src={ph.image_base64} alt="progress" className="w-full h-40 object-cover" />
                  <div className="absolute bottom-0 inset-x-0 bg-black/60 px-2 py-1 text-[10px] text-muted-foreground">{new Date(ph.created_at).toLocaleDateString()}</div>
                  <button data-testid={`photo-delete-${ph.id}`} onClick={() => delPhoto(ph.id)} className="absolute top-1 right-1 bg-black/60 rounded-full p-1 text-muted-foreground hover:text-red-400"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="nutrition" className="mt-4 space-y-3">
          <Card className="rounded-2xl bg-card border-border p-4">
            <p className="text-xs uppercase tracking-wide text-muted-foreground font-semibold mb-3">Log today's diet adherence</p>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">Adherence</span>
              <span className="font-mono text-[#ccff0c] font-semibold">{adForm.adherence_pct}%</span>
            </div>
            <input data-testid="adherence-slider" type="range" min="0" max="100" step="5" value={adForm.adherence_pct} onChange={(e) => setAdForm((f) => ({ ...f, adherence_pct: e.target.value }))} className="w-full accent-[#ccff0c]" />
            <Input data-testid="adherence-note" value={adForm.note} onChange={(e) => setAdForm((f) => ({ ...f, note: e.target.value }))} placeholder="Note (optional)" className="bg-card border-border h-10 mt-3" />
            <Button data-testid="log-adherence" onClick={logAdherence} className="w-full mt-3 bg-[#ccff0c] text-black hover:bg-[#d7ff3a] font-semibold">Log adherence</Button>
          </Card>
          {adherence.length === 0 ? (
            <p className="text-muted-foreground text-sm text-center py-4">No adherence logged yet.</p>
          ) : adherence.map((a) => (
            <Card key={a.id} className="rounded-xl bg-card border-border p-3 flex items-center justify-between">
              <div><p className="font-semibold text-sm">{a.date}</p>{a.note ? <p className="text-xs text-muted-foreground">{a.note}</p> : null}</div>
              <span className="font-mono text-[#ccff0c] font-semibold">{a.adherence_pct}%</span>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="history" className="mt-4 space-y-2">
          {history.length === 0 && runs.length === 0 && <p className="text-muted-foreground text-sm text-center py-6">No activity logged yet.</p>}
          {history.map((h) => (
            <Card key={h.id} className="rounded-xl bg-card border-border p-3 flex items-center gap-3"><Dumbbell className="h-5 w-5 text-[#ccff0c]" /><div className="flex-1"><p className="font-semibold">{h.workout_title}</p><p className="text-xs text-muted-foreground">{new Date(h.created_at).toLocaleDateString()} {h.rpe ? `· RPE ${h.rpe}` : ""}</p></div></Card>
          ))}
          {runs.map((r) => (
            <Card key={r.id} onClick={() => navigate(`/runs/${r.id}`)} className="rounded-xl bg-card border-border p-3 flex items-center gap-3 cursor-pointer"><MapPin className="h-5 w-5 text-[#ccff0c]" /><div className="flex-1"><p className="font-semibold font-mono">{fmtDistance(r.distance_mi, user)} run</p><p className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleDateString()} · {fmtStoredPace(r.avg_pace, user)}</p></div></Card>
          ))}
        </TabsContent>
      </Tabs>
    </AppShell>
  );
}
